// src/services/nftVaultService.js
import {
    getProvider,
    ensureBsc,
    requestAccounts,
    add0x,
    strip0x,
    addrParam,
    u256Param,
    parseUnits,
    formatUnits,
    ethCall,
    ethEstimateGas,
    ethGasPrice,
    sendTransaction,
    waitReceipt,
  } from "@/utils/eth";
  
  // ====== Addresses ======
  export const VAULT = "0x8C3BFe4ADC30A11D4025c9232cE1B66C2bE5ab72";
  export const USDT = "0x55d398326f99059fF775485246999027B3197955";
  export const USDT_DECIMALS = 18;
  
  // ====== Function Selectors ======
  const SEL = {
    balanceOf: "0x70a08231",
    allowance: "0xdd62ed3e",
    approve: "0x095ea7b3",
  
    buy: "0xe9fca283",             // buy(uint256,bytes32)
    latestOrderId: "0x6f3b60d6",   // latestOrderId()
    getOrder: "0xd09ef241",        // getOrder(uint256)
    getOrders: "0x8f72fc77",       // getOrders(uint256,uint256)
    orderExists: "0x86317b96",     // orderExists(uint256)
  };
  
  // ====== Event Topics ======
  const TOPIC = {
    BuyOrderCreated:
      "0x237c44bb6b3526642e4cc30eba76064c577e4362acbc50ee34ea69ccaa8ab604",
  };
  
  // ====== Helpers ======
  function hexToBigInt(hex) {
    return BigInt(add0x(hex));
  }
  
  function readWord(data0x, wordIndex) {
    const h = strip0x(data0x);
    const start = wordIndex * 64;
    return "0x" + h.slice(start, start + 64);
  }
  
  function readU256FromData(data0x, wordIndex) {
    return BigInt(readWord(data0x, wordIndex));
  }
  
  function readBytes32FromData(data0x, wordIndex) {
    return readWord(data0x, wordIndex).toLowerCase();
  }
  
  function readAddressFromData(data0x, wordIndex) {
    const word = strip0x(readWord(data0x, wordIndex));
    return add0x(word.slice(24)).toLowerCase();
  }
  
  function topicToAddress(topic32) {
    const h = strip0x(topic32);
    return add0x(h.slice(24)).toLowerCase();
  }
  
  function bytes32Param(hex0x) {
    const h = strip0x(String(hex0x || ""));
    if (h.length !== 64) throw new Error("clientOrderKey 必须是 32 bytes");
    return h.toLowerCase();
  }
  
  export function randomClientOrderKey() {
    const buf = new Uint8Array(32);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    let hex = "";
    buf.forEach((b) => {
      hex += b.toString(16).padStart(2, "0");
    });
    return "0x" + hex;
  }
  
  export function amountHumanToWei(amountHuman) {
    return parseUnits(String(amountHuman), USDT_DECIMALS);
  }
  
  export function amountWeiToHuman(amountWei, fixed = 2) {
    return formatUnits(BigInt(amountWei), USDT_DECIMALS, fixed);
  }
  
  // ====== Read ======
  export async function getUsdtBalance(owner) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const data = add0x(SEL.balanceOf + addrParam(owner));
    const hex = await ethCall({ to: USDT, data }, "latest", provider);
    return hexToBigInt(hex);
  }
  
  export async function getUsdtBalanceFormatted(owner, fixed = 2) {
    const wei = await getUsdtBalance(owner);
    return formatUnits(wei, USDT_DECIMALS, fixed);
  }
  
  export async function getUsdtAllowance(owner) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const data = add0x(SEL.allowance + addrParam(owner) + addrParam(VAULT));
    const hex = await ethCall({ to: USDT, data }, "latest", provider);
    return hexToBigInt(hex);
  }
  
  export async function getPurchaseSnapshot(owner, amountHuman) {
    const amountWei = amountHumanToWei(amountHuman);
  
    const [balanceWei, allowanceWei] = await Promise.all([
      getUsdtBalance(owner),
      getUsdtAllowance(owner),
    ]);
  
    return {
      owner,
      amountHuman: String(amountHuman),
      amountWei,
      balanceWei,
      balance: formatUnits(balanceWei, USDT_DECIMALS, 2),
      allowanceWei,
      allowance: formatUnits(allowanceWei, USDT_DECIMALS, 2),
      approvedEnough: allowanceWei >= amountWei,
      balanceEnough: balanceWei >= amountWei,
    };
  }
  
  export async function getLatestOrderId() {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const hex = await ethCall({ to: VAULT, data: SEL.latestOrderId }, "latest", provider);
    return hexToBigInt(hex);
  }
  
  export async function getOrder(orderId) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const data = add0x(SEL.getOrder + u256Param(BigInt(orderId)));
    const hex = await ethCall({ to: VAULT, data }, "latest", provider);
  
    return {
      orderId: readU256FromData(hex, 0).toString(),
      user: readAddressFromData(hex, 1),
      amountWei: readU256FromData(hex, 2).toString(),
      amount: formatUnits(readU256FromData(hex, 2), USDT_DECIMALS, 2),
      clientOrderKey: readBytes32FromData(hex, 3),
      timestamp: readU256FromData(hex, 4).toString(),
      mintedVToken: readU256FromData(hex, 5).toString(),
    };
  }

  function toWei(amountHuman) {
    const num = amountHuman.toString();
  
    const [intPart, decPart = ""] = num.split(".");
  
    const intWei = BigInt(intPart) * 10n ** 18n;
  
    const decWei = BigInt((decPart + "000000000000000000").slice(0, 18));
  
    return intWei + decWei;
  }
  
  // ====== Approve ======
  export async function approveUsdt(amountHuman, { onTxHash } = {}) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const bitnwi = toWei(amountHuman);
    const amountWei = amountHumanToWei(bitnwi);

  
    const data = add0x(
      SEL.approve +
        addrParam(VAULT) +
        u256Param(amountWei)
    );
  
    const txBase = { from, to: USDT, data };
    const gas = await ethEstimateGas(txBase, provider);
    const gasPrice = await ethGasPrice(provider);
  
    const { hash } = await sendTransaction({ ...txBase, gas, gasPrice }, provider);
    onTxHash && onTxHash(hash);
  
    const receipt = await waitReceipt(hash, {}, provider);
    return { hash, receipt };
  }
  
  // ====== Buy ======
  export async function buyNft(amountHuman, clientOrderKey, { onTxHash } = {}) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const amountWei = amountHumanToWei(amountHuman);
  
    const data = add0x(
      SEL.buy +
        u256Param(amountWei) +
        bytes32Param(clientOrderKey)
    );
  
    const txBase = { from, to: VAULT, data };
    const gas = await ethEstimateGas(txBase, provider);
    const gasPrice = await ethGasPrice(provider);
  
    const { hash } = await sendTransaction({ ...txBase, gas, gasPrice }, provider);
    onTxHash && onTxHash(hash);
  
    const receipt = await waitReceipt(hash, {}, provider);
    return { hash, receipt };
  }
  
  // ====== One-shot ======
  export async function placeNftOrder({
    amountHuman,
    clientOrderKey,
    onStep,
  } = {}) {
    if (!amountHuman || Number(amountHuman) <= 0) {
      throw new Error("购买金额不能为空");
    }
  
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const key = clientOrderKey || randomClientOrderKey();
  
    const snapshot = await getPurchaseSnapshot(from, amountHuman);
  
    onStep && onStep("SNAPSHOT_READY", {
      from,
      snapshot,
      clientOrderKey: key,
    });
  
    if (!snapshot.balanceEnough) {
      throw new Error(`USDT余额不足，当前余额 ${snapshot.balance}，购买需要 ${snapshot.amountHuman}`);
    }
  
    if (!snapshot.approvedEnough) {
      onStep && onStep("APPROVE_REQUIRED", {
        allowance: snapshot.allowance,
        need: snapshot.amountHuman,
      });
  
      const { hash: approveHash, receipt: approveReceipt } = await approveUsdt(amountHuman, {
        onTxHash: (h) => onStep && onStep("APPROVE_TX", { hash: h }),
      });
  
      onStep && onStep("APPROVE_CONFIRMED", {
        hash: approveHash,
        status: approveReceipt?.status,
      });
    } else {
      onStep && onStep("APPROVE_SKIPPED", {
        allowance: snapshot.allowance,
      });
    }
  
    onStep && onStep("BUY_SENDING", {
      amountHuman,
      clientOrderKey: key,
    });
  
    const { hash: buyHash, receipt: buyReceipt } = await buyNft(amountHuman, key, {
      onTxHash: (h) => onStep && onStep("BUY_TX", { hash: h }),
    });
  
    const parsed = parseBuyOrderCreatedFromReceipt(buyReceipt);
  
    onStep && onStep("BUY_CONFIRMED", {
      hash: buyHash,
      status: buyReceipt?.status,
      parsed,
    });
  
    return {
      from,
      clientOrderKey: key,
      buyHash,
      buyReceipt,
      parsed,
    };
  }
  
  // ====== Parse Receipt ======
  export function parseBuyOrderCreatedFromReceipt(receipt) {
    if (!receipt || !Array.isArray(receipt.logs)) return null;
  
    const vaultAddr = VAULT.toLowerCase();
  
    const hits = receipt.logs.filter((lg) => {
      const addrOk = (lg.address || "").toLowerCase() === vaultAddr;
      const t0 = lg.topics && lg.topics[0] ? String(lg.topics[0]).toLowerCase() : "";
      return addrOk && t0 === TOPIC.BuyOrderCreated.toLowerCase();
    });
  
    if (!hits.length) return null;
  
    const lg = hits[0];
    const orderId = BigInt(lg.topics[1]);
    const user = topicToAddress(lg.topics[2]);
    const clientOrderKey = lg.topics[3].toLowerCase();
  
    const amount = readU256FromData(lg.data, 0);
    const mintedVToken = readU256FromData(lg.data, 1);
  
    return {
      orderId: orderId.toString(),
      user,
      clientOrderKey,
      amountWei: amount.toString(),
      amount: formatUnits(amount, USDT_DECIMALS, 2),
      mintedVToken: mintedVToken.toString(),
    };
  }
  
  export default {
    VAULT,
    USDT,
    USDT_DECIMALS,
    randomClientOrderKey,
    amountHumanToWei,
    amountWeiToHuman,
    getUsdtBalance,
    getUsdtBalanceFormatted,
    getUsdtAllowance,
    getPurchaseSnapshot,
    getLatestOrderId,
    getOrder,
    approveUsdt,
    buyNft,
    placeNftOrder,
    parseBuyOrderCreatedFromReceipt,
  };